import store from "@/store/store";
import { AdminTemplateOptions, Layout } from "piramis-base-components/src/plugins/AdminTemplate/includes/types/Config";
import { ProductNames } from "piramis-base-components/src/plugins/AdminTemplate/components/OurProducts/types";
import { EntityTypes } from "piramis-base-components/src/components/SelectEntityWizard/includes/types";
import { MenuItemType } from "piramis-base-components/src/plugins/AdminTemplate/includes/types/MenuItem";
import { hasMatchedRouteParams } from "piramis-base-components/src/components/SelectEntityWizard/includes/helpers";
import { SlimBoardInfo, TestBoardStateCode } from "@/includes/types/Board.types";
import TokenId from "piramis-base-components/src/shared/utils/TokenId";
import { hasChangeConfigPermission } from "@/includes/PermissionHelper";
import i18n from "@/i18n/i18n";
import { NavigationGuardNext } from "vue-router";
import { SlimChannelInfo } from "@/includes/types/Channel.types";
import { errorNotification } from "@/includes/services/NotificationService";
import { navbar } from "./navbar";
import { routes } from "./routes";
import { sidebar } from "./sidebar";

export const BaseTemplateConfig: AdminTemplateOptions['config'] = {
  navbar,
  routes,
  sidebar,
  feedbackItems: [
    {
      menuItem: {
        type: MenuItemType.DropDownLink,
        isVisible: [ true ],
        url: () => 'https://www.youtube.com/@user-gz2go8dj9o',
        featherIcon: true,
        icon: 'icon-youtube',
        name: 'menu_help_youtube_title'
      }
    },
    {
      menuItem: {
        isVisible: [ true ],
        type: MenuItemType.DropDownLink,
        name: 'menu_help_tech_support_title',
        icon: 'icon-life-buoy',
        featherIcon: true,
        url: () => 'https://t.me/PressCodeSupportRu'
      },
    },
  ],
  footer: {
    version: store.state.metaModule.PACKAGE_VERSION,
    ourProducts: {
      exclude: [
        ProductNames.PRESSCODEBOT
      ],
    }
  },
  meta: {
    projectName: 'PressCode.app',
    apiUrl: 'api.presscode.app/cabinet',
    projectDomain: 'https://presscode.app',
    sidebarLogo: require('../../assets/images/presscode-logo/presscodebot_logo.svg'),
    loginPageLogo: require('../../assets/images/presscode-logo/logo_title.png'),
    tgResolvers: {
      supportBotDomain: 'PressCodeSupportRu',
      promotionBotDomain: 'ChatKeeperPromotionBot',
      mainBotDomain: 'Presscodebot',
    },
    login: {
      botAuthLink: 'https://t.me/PresscodeBot?start=cabinetlink',
      loginWidget: {
        botLogin: 'presscodebot',
        tgWidgetAuthLink: 'u',
      }
    },
    languageCodes: [ 'ru', 'en' ],
    isSaveButtonVisible: false,
    paymentEntity: {
      target_type: (ctx) => {
        if (store.state.boardsState.boards) {
          const currentBoard: SlimBoardInfo | null | undefined = store.getters.getBoardById(ctx.route.params[ EntityTypes.BOT_ID ])

          if (currentBoard) {
            const channel_id = ctx.route.params[EntityTypes.CHANNEL_ID]

            return {
              target_id: {
                board_key: currentBoard.board,
                ...channel_id ? { channel_id } : {},
              },
              target_type: channel_id ? 'channel' : 'board'
            }
          }
        }

        throw new Error('Boards are not initialised')
      }
    },
  },
  loyaltySystem: {
    partnerBlock: {
      partnerLink: (localeCode) => 'https://t.me/ChatKeeperPromotion/?start=loyalty_parnters'
    },
    companiesBlock: {
      termsOwnerLink: (localeCode) => 'https://t.me/ChatKeeperPromotion/?start=loyalty_parnters'
    }
  },
  companyRegistration: {
    powerOfAttorney: (localeCode): string => 'https://presscode.app/wp-content/uploads/2022/03/power_of_attorney_ur.doc',
    howToRegisterUr: (localeCode): string => 'https://presscode.app/wp-content/uploads/2022/03/how_to_register_ur.docx',
  },
  search: {
    entities: (ctx) => {
      const boards = store.state.boardsState.boards

      if (boards !== null && Array.isArray(boards) && boards.length) {
        return boards.map((b) => {
          return {
            id: TokenId.getId(b.board),
            avatar: b.avatar,
            title: b.title
          }
        })
      } else {
        return []
      }
    },
    path: (ctx, router, selectedId): void => {
      if (selectedId !== null && ctx.route.params[ EntityTypes.BOT_ID ] !== selectedId.toString()) {
        const currentBoard: SlimBoardInfo | null | undefined = store.getters.getBoardById(selectedId)

        store.dispatch('getBoardConfig', currentBoard!.board)
            .then(() => {
              router.push({
                name: 'posts_planner',
                params: {
                  [ EntityTypes.BOT_ID ]: selectedId.toString(),
                }
              })

              store.dispatch('clearBotStores')
            })
      }
    }
  },
  serviceData: {
    type: 'ServiceData',
    tariffs: {
      Business: {
        type: 'TariffInfo',
        img: require('../../assets/images/payments/tariffs/business.png'),
        color: 'rgb(177,69,250)',
      },
      Prime: {
        type: 'TariffInfo',
        img: require('../../assets/images/payments/tariffs/business.png'),
        color: 'rgb(177,69,250)',
      },
    },
  },
  profile: {
    extraAuth: true
  },
  sidebarMenu: [],
  globalGuards: {
    afterEach: async (to) => {
      const boardId = to.params[ EntityTypes.BOT_ID ]
      const channelId = to.params[EntityTypes.CHANNEL_ID]

      if (!boardId)  {
        await store.dispatch('clearBotStores')
      }

      if (!channelId) {
        await store.dispatch('reset_active_channel')

        await store.dispatch('resetInviteLinks')
      }
    },
    beforeEach: async (to, from, next) => {
      const boardId = to.params[ EntityTypes.BOT_ID ]
      const channelId = to.params[EntityTypes.CHANNEL_ID]

      const getChannel = async (next: NavigationGuardNext, channelId: SlimChannelInfo['id'], board: SlimBoardInfo['board'], boardId: string) => {
        const channel = await store.dispatch('getChannelConfig', {
          board_key: board,
          channel_id: channelId
        })

        if (channel) {
          await store.dispatch('updateTargetsWithoutLinkedGroup', [ channelId ])

          store.commit('updateValidateCommentButtonHasGroup', true)
        } else {
          store.commit('updateValidateCommentButtonHasGroup', false)

          next({ name: 'Channels', params: { [EntityTypes.BOT_ID]: boardId } })
        }
      }

      if (hasMatchedRouteParams(to)) {
        next()

        return
      }

      if (!store.state.boardsState.boards) {
        await store.dispatch('requestBoards')
      }

      if (boardId && !store.getters.isBoardSet) {
        const currentBoard: SlimBoardInfo | undefined = store.getters.getBoardById(boardId)

        if (currentBoard) {
          const board = await store.dispatch('getBoardConfig', currentBoard.board)

          if (board) {
            store.commit('AdminTemplate/SET_ACCOUNT_DATA_ROOT', boardId)

            if (!store.state.menuOptionsState.menuOptions && hasChangeConfigPermission()) {
              await store.dispatch('getMenuOptions')
            }

            if (channelId && !store.getters.isChannelSet) {
              await getChannel(next, +channelId, board.board, boardId)
            }
          } else {
            next({ name: 'Boards' })
          }
        } else {
          errorNotification(i18n.t('bot_not_found_error_message').toString())

          next({ name: 'Boards' })
        }
      } else if (store.getters.isBoardSet && channelId && !store.getters.isChannelSet) {
        const currentBoard: SlimBoardInfo | undefined = store.getters.getBoardById(boardId)

        if (currentBoard) {
          await getChannel(next, +channelId, currentBoard.board, boardId)
        }
      }

      next()
    },
  },
  onSaveButtonClick: (ctx, wasClicked): void => {
    if (wasClicked) {
      if (ctx.route.params[EntityTypes.CHANNEL_ID]) {
        store.dispatch('save_active_channel_config', ctx.route)
      } else {
        store.dispatch('savePostBoardConfig')
      }
    }
  }
}